import React, { RefObject, useEffect, useRef, useState } from "react";
import styled from "styled-components";

export type BalloonProps<T extends Element = Element> = {
    targetRef: RefObject<T | null>;
    children: React.ReactNode;
    visible?: boolean;
    style?: React.CSSProperties;
    className?: string;
    onHover?: boolean;
    onChange?: (visible: boolean, ref: T) => void;
};

const BalloonArrow = styled.span`
    position: absolute;
    top: -0.6em;
    left: calc(50% - 0.3em);
    width: 0;
    height: 0;
    border-left: 0.6em solid transparent;
    border-right: 0.6em solid transparent;
    border-bottom: 0.6em solid #444;
    pointer-events: none;
`;

const Positioner = styled.div`
    position: absolute;
    z-index: 30;
`;

const BalloonContainer = styled.div`
    background: red;
    min-width: 200px;
    max-width: 300px;
    background-color: #444;
    color: white;
    padding: 0.6em;
    border-radius: 0.5em;
    transform-origin: top center;
    animation: check 200ms;
    font-size: 0.9em;
    transition: 100ms;
    box-shadow: 2px 2px 10px #0005;

    &:hover {
        pointer-events: all;
        transition: 100ms;
        opacity: 0.5;
    }
`;

export function Balloon(props: BalloonProps) {
    const [visible, setVisible] = useState(props.visible ?? false);
    const balloonRef = useRef<HTMLDivElement>(null);
    let yOffset = 8;

    function onEnter() {
        setVisible(true);
    }

    function onExit() {
        setVisible(false);
    }

    useEffect(() => {
        if (props.visible !== undefined) setVisible(props.visible);
    }, [props.visible]);

    useEffect(() => {
        function onScroll() {
            let b = balloonRef.current;
            let t = props.targetRef.current;
            if (b && t) {
                let targetRect = t.getBoundingClientRect();
                let balloonRect = b.getBoundingClientRect();
                b.style.top = targetRect.top + window.scrollY + targetRect.height + yOffset + "px";
                b.style.left = targetRect.left + targetRect.width / 2 - balloonRect.width / 2 + "px";
            }
        }

        if (props.targetRef.current) props.onChange?.(visible, props.targetRef.current);

        if (visible) {
            document.addEventListener("scroll", onScroll);
            document.addEventListener("resize", onScroll);
            onScroll();
            return () => {
                document.removeEventListener("scroll", onScroll);
                document.removeEventListener("resize", onScroll);
            };
        }
    }, [visible, props.targetRef, balloonRef, props.onChange, yOffset]);

    useEffect(() => {
        if (props.targetRef && props.onHover) {
            let t = props.targetRef.current!;
            t.addEventListener("mouseenter", onEnter);
            t.addEventListener("mouseleave", onExit);
            return () => {
                t.addEventListener("mouseenter", onEnter);
                t.addEventListener("mouseleave", onExit);
            };
        }
    }, [props.targetRef, props.onHover]);

    return (
        <Positioner ref={balloonRef}>
            {visible && (
                <BalloonContainer onClick={() => setVisible(false)} className={props.className} style={props.style}>
                    <BalloonArrow />
                    {props.children}
                </BalloonContainer>
            )}
        </Positioner>
    );
}

export const ErrorBalloon = styled(Balloon)`
    background-color: #ec454f;
    color: white;
    font-weight: bold;

    > span {
        border-bottom: 0.7em solid #ec454f;
    }
`;

export const WarningBalloon = styled(Balloon)`
    background-color: #bd881e;
    color: white;
    font-weight: bold;

    > span {
        border-bottom: 0.7em solid #bd881e;
    }
`;
